import { Card, CardContent } from '@/components/ui/shadcn/card';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { Calendar, Package, User } from 'lucide-react';
import { route } from 'ziggy-js';
import { BugReportSeverityBadge, BugReportStatusBadge } from './bug-report-badges';
import { type BugReportListItem } from './types';

interface BugReportCardProps {
    report: BugReportListItem;
    // Opening a report requires bug-reports.update; read-only viewers get a
    // non-clickable card so they never land on a 403.
    canOpen: boolean;
}

export function BugReportCard({ report, canOpen }: BugReportCardProps) {
    const { t } = useTranslations();

    const publicId = report.public_id ?? report.id.slice(0, 8);
    const severity = report.severity_final ?? report.severity_suggested;

    const card = (
        <Card className={cn('mb-2 transition-shadow', canOpen && 'cursor-pointer hover:shadow-sm')}>
            <CardContent className="p-3">
                <div className="flex flex-col gap-2">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-semibold">{publicId}</h3>
                        </div>
                        <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5">
                            <BugReportStatusBadge status={report.status} />
                            {severity && <BugReportSeverityBadge severity={severity} />}
                        </div>
                    </div>

                    <p className="text-muted-foreground line-clamp-3 text-sm">{report.description}</p>

                    {/* Details */}
                    <div className="space-y-1">
                        {report.module && (
                            <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                <Package className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="truncate">{report.module}</span>
                            </div>
                        )}

                        <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                            <User className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="truncate">{report.reporter?.name ?? t('bug_reports.table.unknown_reporter', 'Unknown')}</span>
                        </div>

                        {report.created_at && (
                            <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                                <span>{new Date(report.created_at).toLocaleDateString()}</span>
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    if (!canOpen) {
        return card;
    }

    return (
        <Link href={route('admin.bug-reports.show', report.id)} className="block">
            {card}
        </Link>
    );
}
